import { CalendarModeRangeType } from "@custom-types/Types";
import {
  Order,
  toChartType
} from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import {
  getChartData,
  getChartIcon,
  getRuleType
} from "@hooks/analytics/useLocationRuleGroupByRuleType";
import { useLang } from "@hooks/useLang";
import { useMemo } from "react";
import { useCurrentTimezone } from "@hooks/useCurrentTimezone";
import _ from "lodash";
import moment from "moment";
import * as S from "../AnalyticsPage.styled";
import ChartCard from "./ChartCard";

interface ChartListProps {
  searchFilterOrder: Order[];
  listKey: string;
  handleShowChat: (title: string) => void;
  isSingleLocation: boolean;
  searchFilterBaseTime?: {
    start: string;
    end: string;
    mode: CalendarModeRangeType;
  };
  ruleGroup: Record<
    string,
    _.Dictionary<
      {
        locationId: string;
        locationName: string;
        deviceId: string;
        ruleName: string;
        channel: number;
        ruleType: string;
        cameraName: string;
      }[]
    >
  >;
  dropdownList: {
    label: string;
    value: string;
  }[];
  selectedLocationId: string;
  timezone?: string;
}

const ChartList = ({
  searchFilterOrder,
  listKey,
  isSingleLocation,
  searchFilterBaseTime,
  handleShowChat,
  ruleGroup,
  dropdownList,
  selectedLocationId,
  timezone
}: ChartListProps) => {
  const { translate } = useLang();
  const { convertByTimezone } = useCurrentTimezone();
  const baseTime = useMemo(() => {
    if (!searchFilterBaseTime) return null;
    return {
      start: convertByTimezone(
        moment
          .tz(searchFilterBaseTime.start as string, timezone as string)
          .startOf("day")
          .toISOString(true),
        timezone
      ),
      end: convertByTimezone(
        moment
          .tz(searchFilterBaseTime.end as string, timezone as string)
          .endOf("day")
          .toISOString(true),
        timezone
      ),
      mode: searchFilterBaseTime.mode as CalendarModeRangeType
    };
  }, [searchFilterBaseTime, timezone]);

  const rulesByChartType = useMemo(() => {
    if (!searchFilterOrder) return {};

    const map: Record<
      string,
      _.Dictionary<
        {
          locationId: string;
          locationName: string;
          deviceId: string;
          ruleName: string;
          channel: number;
          ruleType: string;
          cameraName: string;
        }[]
      >
    > = {};

    searchFilterOrder.forEach((orderType) => {
      const chartType = toChartType(orderType);
      const ruleType = getRuleType(chartType);

      if (!ruleType) return;

      let rules;
      const DEFAULT_SELECTED_ANALYTICS_LOCATION = {
        name: translate("all_locations"),
        id: "SAIBGCHDTEMFIGNHD"
      };
      if (selectedLocationId === DEFAULT_SELECTED_ANALYTICS_LOCATION.id) {
        rules = ruleGroup[ruleType];
      } else {
        const locationName = dropdownList.find(
          (loc) => loc.value === selectedLocationId
        )?.label;

        if (locationName) {
          rules = _.pick(ruleGroup[ruleType], locationName);
        }
      }

      if (!_.isEmpty(rules)) {
        map[chartType] = rules;
      }
    });

    return map;
  }, [searchFilterOrder, JSON.stringify(ruleGroup), selectedLocationId]);

  return (
    <S.ChartList>
      {searchFilterOrder.map((orderType) => {
        const chartType = toChartType(orderType);
        const rules = rulesByChartType[chartType];

        if (!rules) return null;
        if (!baseTime) return null;

        const viewListTypes = getChartData(chartType);
        const ChartIcon = getChartIcon(chartType);

        return (
          <ChartCard
            cardKey={`${chartType}_${listKey}`}
            key={`${chartType}_${listKey}`}
            type={chartType}
            rules={rules}
            icon={ChartIcon}
            title={translate(`analytics_chart_name.${chartType.toLowerCase()}`)}
            onChangeInsightChat={handleShowChat}
            baseTime={baseTime}
            options={{ viewList: viewListTypes }}
            isSingleLocation={isSingleLocation}
            timeZone={timezone}
          />
        );
      })}
    </S.ChartList>
  );
};

export default ChartList;
